/**
 * App tokens — the third principal (architecture §2). Pure: Web Crypto only, no Convex imports,
 * so the smoke test and unit tests can sign and verify tokens with the same code the backend runs.
 *
 * Format: `<base64url(JSON claims)>.<base64url(HMAC-SHA256(APP_TOKEN_SECRET, first part))>`.
 * No header, no key id: one secret per deployment, one algorithm. Claims:
 *   { v: 1, appId, circleId, scope: 'member' | 'system', memberId?, deviceId?, iat, exp }
 * `memberId` and `deviceId` are set for `member` scope and absent for `system`. Times are epoch ms.
 * Tokens are stateless: nothing is stored; verification recomputes the HMAC and checks the lifetime and `exp`,
 * then the caller (`requireAppToken`) loads app, circle, member and device and re-checks them.
 */

export const APP_TOKEN_VERSION = 1;
/** Default and maximum lifetime of a token. */
export const APP_TOKEN_TTL_MS = 5 * 60 * 1000;
/** `APP_TOKEN_SECRET` and `DISPATCHER_SECRET` must be at least this long (32 random bytes base64url is 43). */
export const MIN_SECRET_LENGTH = 32;
/** `createdBy` / `updatedBy` of a write made with a `system`-scoped token (no member behind it). */
export const APP_ACTOR_ID = 'app' as const;

export type AppTokenScope = 'member' | 'system';

export type AppTokenClaims = {
  v: typeof APP_TOKEN_VERSION;
  appId: string;
  circleId: string;
  scope: AppTokenScope;
  memberId?: string;
  deviceId?: string;
  iat: number;
  exp: number;
};

/** How far in the future `iat` may lie (clock skew between the signer and the verifier). */
export const APP_TOKEN_CLOCK_SKEW_MS = 60_000;

export type VerifyFailure = 'malformed' | 'bad_signature' | 'lifetime' | 'expired';
export type VerifyResult = { ok: true; claims: AppTokenClaims } | { ok: false; reason: VerifyFailure };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) return null;
  const padded = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  try {
    const binary = atob(padded);
    const out = new Uint8Array(new ArrayBuffer(binary.length));
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

async function hmacKey(secret: string, usage: 'sign' | 'verify'): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [usage]);
}

/** Signs `claims` as they are (no defaults, no clamping — `mintAppToken` fills them in). */
export async function signAppToken(claims: AppTokenClaims, secret: string): Promise<string> {
  const payload = toBase64Url(encoder.encode(JSON.stringify(claims)));
  const sig = await crypto.subtle.sign('HMAC', await hmacKey(secret, 'sign'), encoder.encode(payload));
  return `${payload}.${toBase64Url(new Uint8Array(sig))}`;
}

function isClaims(x: unknown): x is AppTokenClaims {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
  const c = x as Record<string, unknown>;
  if (c.v !== APP_TOKEN_VERSION) return false;
  if (typeof c.appId !== 'string' || typeof c.circleId !== 'string') return false;
  if (c.scope !== 'member' && c.scope !== 'system') return false;
  if (typeof c.iat !== 'number' || typeof c.exp !== 'number' || !Number.isFinite(c.iat) || !Number.isFinite(c.exp)) return false;
  if (c.scope === 'member') {
    if (typeof c.memberId !== 'string' || typeof c.deviceId !== 'string') return false;
  } else if (c.memberId !== undefined || c.deviceId !== undefined) {
    return false;
  }
  return true;
}

/**
 * Checks the signature (constant time, through `crypto.subtle.verify`), the shape of the claims, the lifetime
 * and `exp`. `exp` is exclusive: a token is valid while `now < exp`. The TTL cap is enforced here, not only at
 * mint, so a token signed directly with the secret cannot live longer: `0 < exp - iat <= APP_TOKEN_TTL_MS`
 * and `iat <= now + APP_TOKEN_CLOCK_SKEW_MS`, else `lifetime`.
 */
export async function verifyAppToken(token: string, secret: string, now: number): Promise<VerifyResult> {
  if (typeof token !== 'string' || token.length > 4096) return { ok: false, reason: 'malformed' };
  const parts = token.split('.');
  if (parts.length !== 2) return { ok: false, reason: 'malformed' };
  const [payload, sigText] = parts as [string, string];
  const sig = fromBase64Url(sigText);
  if (payload.length === 0 || !sig || sig.length !== 32 || !/^[A-Za-z0-9_-]+$/.test(payload)) {
    return { ok: false, reason: 'malformed' };
  }
  const valid = await crypto.subtle.verify('HMAC', await hmacKey(secret, 'verify'), sig, encoder.encode(payload));
  if (!valid) return { ok: false, reason: 'bad_signature' };
  const bytes = fromBase64Url(payload);
  if (!bytes) return { ok: false, reason: 'malformed' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes));
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (!isClaims(parsed)) return { ok: false, reason: 'malformed' };
  if (parsed.exp <= parsed.iat || parsed.exp - parsed.iat > APP_TOKEN_TTL_MS || parsed.iat > now + APP_TOKEN_CLOCK_SKEW_MS) {
    return { ok: false, reason: 'lifetime' };
  }
  if (parsed.exp <= now) return { ok: false, reason: 'expired' };
  return { ok: true, claims: parsed };
}

/** Reads the claims without checking anything. For logs and tests; never for authorisation. */
export function decodeAppTokenUnsafe(token: string): AppTokenClaims | null {
  const payload = token.split('.')[0] ?? '';
  const bytes = fromBase64Url(payload);
  if (!bytes) return null;
  try {
    const parsed: unknown = JSON.parse(decoder.decode(bytes));
    return isClaims(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** True when `secret` can sign tokens: a string of at least MIN_SECRET_LENGTH characters. */
export function isUsableSecret(secret: string | undefined): secret is string {
  return typeof secret === 'string' && secret.length >= MIN_SECRET_LENGTH;
}

/**
 * Constant-time equality of two shared secrets. Both are hashed first, so the comparison runs over
 * fixed-length digests and leaks neither the length nor the position of the first difference.
 */
export async function secretsMatch(given: string, expected: string): Promise<boolean> {
  if (typeof given !== 'string' || typeof expected !== 'string') return false;
  const [a, b] = await Promise.all([
    crypto.subtle.digest('SHA-256', encoder.encode(given)),
    crypto.subtle.digest('SHA-256', encoder.encode(expected)),
  ]);
  const ua = new Uint8Array(a);
  const ub = new Uint8Array(b);
  let diff = ua.length ^ ub.length;
  for (let i = 0; i < ua.length; i++) diff |= ua[i]! ^ (ub[i] ?? 0);
  return diff === 0;
}
